import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  VideoFile as VideoIcon
} from '@mui/icons-material';
import React, { useEffect, useState } from 'react';

import api from '../../api/axiosInstance';
import { useProfile } from '../../providers/ProfileProvider';

// File types matching the model enum with labels and help text
const FILE_TYPES = [
  {
    value: 'identification/legal',
    label: 'Identification & Legal',
    helpText: 'Use this category for official identification and legal documents such as passports, national IDs, work permits, visas, and tax-related documents.'
  },
  {
    value: 'contracts',
    label: 'Contracts & Agreements',
    helpText: 'Upload signed contracts, subcontractor agreements, statements of work, or any other formal agreements you have entered into.'
  },
  {
    value: 'cv',
    label: 'CV & Profile',
    helpText: 'Use this category for your CV or résumé, cover letters, skill summaries, portfolios in document form, and other files that describe your professional profile.'
  },
  {
    value: 'certifications/training',
    label: 'Certifications & Training',
    helpText: 'Upload certificates, course diplomas, licenses, or proof of completed training and professional development.'
  },
  {
    value: 'projects',
    label: 'Work Samples & Projects',
    helpText: 'Use this category for examples of your work, including case studies, code samples, design files, project documentation, or written descriptions of past projects.'
  },
  {
    value: 'media',
    label: 'Media & Portfolio Assets',
    helpText: 'Upload images, videos, presentations, demo recordings, or other media used to showcase your work or portfolio.'
  },
  {
    value: 'assessments',
    label: 'Assessments & Tests',
    helpText: 'Use this category for coding tests, technical assignments, take-home tasks, or any evaluation material requested by Native Coders.'
  },
  {
    value: 'personal',
    label: 'Personal & Administrative',
    helpText: 'Upload personal administrative documents such as availability statements, bank or payment information (if requested), or other personal records related to your engagement.'
  },
  {
    value: 'other',
    label: 'Other',
    helpText: 'Use this category only if your document does not fit into any of the categories above.'
  }
];

// Helper function to get file type info
const getFileTypeInfo = (value) => {
  return FILE_TYPES.find(ft => ft.value === value) || FILE_TYPES.find(ft => ft.value === 'other');
};

// Derive a display type from the MIME type stored in the backend model
const getDisplayTypeFromMimetype = (mimetype) => {
  if (!mimetype) return 'other';
  if (mimetype === 'application/pdf') return 'pdf';
  if (mimetype.startsWith('image/')) return 'image';
  if (mimetype.startsWith('video/')) return 'video';
  if (
    mimetype === 'application/msword' ||
    mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimetype === 'application/vnd.oasis.opendocument.text'
  ) return 'document';
  return 'other';
};

const FilesAndMedia = () => {
  const profile = useProfile();
  
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [myFiles, setMyFiles] = useState([]);
  const [companyFiles, setCompanyFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  

  const [uploadData, setUploadData] = useState({
    file: null,
    description: '',
    category: 'other'
  });


  useEffect(() => {
    if (activeTab === 0) {
      fetchMyFiles();
    } else {
      fetchCompanyFiles();
    }
  }, [activeTab, profile]);

  const fetchMyFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/files/');
      setMyFiles(response.data?.data);
    } catch (error) {
      console.error('Error fetching my files:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyFiles = () => {
    // No backend endpoint exists for company files yet
    setCompanyFiles([]);
  };

  const handleUploadFile = async () => {
    if (!uploadData.file) {
      showSnackbar('Please select a file', 'error');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('fileType', uploadData.category);
      if (uploadData.description) {
        formData.append('description', uploadData.description);
      }

      await api.post('/files/upload', formData);

      showSnackbar('File uploaded successfully', 'success');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchMyFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar(error.response?.data?.message || 'Error uploading file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleCloseMenu();
  };

  const resetUploadForm = () => {
    setUploadData({
      file: null,
      description: '',
      category: 'other'
    });
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleOpenMenu = (event, file) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom, left: rect.right });
    setSelectedFile(file);
  };

  const handleCloseMenu = () => {
    setMenuPosition(null);
    setSelectedFile(null);
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await api.delete(`/files/${fileToDelete._id}`);
      setMyFiles(prev => prev.filter(f => f._id !== fileToDelete._id));
      showSnackbar('File deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting file', 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      // fileToDelete is cleared in TransitionProps.onExited to avoid
      // a flash of "undefined" while the dialog animates out
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getCurrentFiles = () => {
    const files = activeTab === 0 ? myFiles : companyFiles;

    if (!searchTerm) return files;
    const lower = searchTerm.toLowerCase();
    return files.filter(file =>
      file.originalName?.toLowerCase().includes(lower) ||
      file.description?.toLowerCase().includes(lower) ||
      file.fileType?.toLowerCase().includes(lower) ||
      getFileTypeInfo(file.fileType)?.label?.toLowerCase().includes(lower)
    );
  };

  const getFileIcon = (displayType) => {
    switch (displayType) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'image':
        return <ImageIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'document':
        return <DocumentIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'video':
        return <VideoIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      default:
        return <FileIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const FileCard = ({ file, showUploadedBy = false }) => (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 2,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        minHeight: '80px',
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box sx={{ mr: 2, mt: 0.5 }}>
          {getFileIcon(getDisplayTypeFromMimetype(file.mimetype))}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '12px' }}>
              {file.originalName}
              <Chip
                label={getFileTypeInfo(file.fileType)?.label || file.fileType || 'Other'}
                size="small"
                sx={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  fontSize: '6px',
                  height: '12px',
                  fontWeight: 500,
                  borderRadius: '100px',
                  border: '1px solid #dee2e6',
                  ml: 1
                }}
              />
            </Typography>
          </Box>

          {file.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, lineHeight: 1, fontSize: '10px' }}
            >
              {file.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FileIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" color="text.secondary">
                {file.originalName} ({formatFileSize(file.size)})
              </Typography>
            </Box>
            
            {showUploadedBy && file.uploadedBy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {file.uploadedBy.firstName} {file.uploadedBy.lastName}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatDate(file.uploadedAt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {activeTab === 0 && (
        <IconButton
          size="small"
          sx={{ ml: 1, alignSelf: 'flex-start' }}
          onClick={(e) => handleOpenMenu(e, file)}
        >
          <MoreVertIcon />
        </IconButton>
      )}
    </Paper>
  );

  const files = getCurrentFiles();

  return (
    <div>
      <div>
        {/* Header */}
        <div style={{ marginBottom: '0.5rem' }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 1,
              fontSize: '24px',
              color: '#000000',
              lineHeight: '2rem',
            }}
          >
            Files & Media
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 'normal',
              fontSize: '14px',
            }}
          >
            Manage and share documents, contracts, and project files
          </Typography>
        </div>

        {/* Tab Navigation */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                color: 'text.secondary'
              },
              '& .Mui-selected': {
                color: 'primary.main !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3
              }
            }}
          >
            <Tab label="MY UPLOADED FILES & MEDIA" />
            <Tab label="NATIVE CODERS FILES & MEDIA" />
          </Tabs>
        </Box>
        
        {/* Action Bar */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 1,
          alignItems: 'center',
          maxWidth: '70rem'
        }}>
          <TextField
            placeholder="Search files by name or comment"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '44px'
              }
            }}
          />

          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                height: '44px',
                px: 3,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              UPLOAD FILE
            </Button>
          )}
        </Box>

        {/* File Count */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontSize: '14px' }}
        >
          Showing {files.length} file{files.length !== 1 ? 's' : ''}
        </Typography>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* File Cards */}
        {!loading && (
          <Box sx={{ maxWidth: '70rem' }}>
            {files.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 'No files found matching your search' : 'No files uploaded yet'}
                </Typography>
                {activeTab === 0 && !searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Upload Your First File
                  </Button>
                )}
              </Paper>
            ) : (
              files.map((file) => (
                <FileCard 
                  key={file._id} 
                  file={file} 
                  showUploadedBy={activeTab === 1}
                />
              ))
            )}
          </Box>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload File
          <IconButton
            onClick={() => setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
            >
              {uploadData.file ? uploadData.file.name : 'Select File'}
              <input
                type="file"
                hidden
                onChange={handleFileSelect}
              />
            </Button>

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={uploadData.description}
              onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add a description (optional)"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={uploadData.category}
                label="Category"
                onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
              >
                {FILE_TYPES.map((fileType) => (
                  <MenuItem key={fileType.value} value={fileType.value}>
                    {fileType.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {getFileTypeInfo(uploadData.category)?.helpText}
              </FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUploadFile} 
            variant="contained"
            disabled={!uploadData.file || loading}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        open={Boolean(menuPosition)}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ?? undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 160, borderRadius: 2 } } }}
      >
        <MenuItem onClick={() => selectedFile && handleDownloadFile(selectedFile.url, selectedFile.originalName)}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedFile && handleDeleteClick(selectedFile)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionProps={{ onExited: () => setFileToDelete(null) }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, pb: 1 }}>
          Delete file?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText sx={{ fontSize: '14px' }}>
            <strong>{fileToDelete?.originalName}</strong> will be permanently deleted and cannot be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined" size="small" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" size="small" color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FilesAndMedia;